import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Shield, Filter, RefreshCw, User, Activity } from "lucide-react";

const ENTITY_TONE = {
  claim:    "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  workshop: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  user:     "bg-orange-500/10 text-orange-700 dark:text-orange-300",
};

export default function AuditTrail() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filters, setFilters] = useState({ actor_email: "", entity: "", action: "", entity_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).toString();
      const [logsR, sumR] = await Promise.all([
        api.get(`/admin/audit/logs${qs ? `?${qs}` : ""}`),
        api.get(`/admin/audit/summary`),
      ]);
      setData(logsR.data);
      setSummary(sumR.data);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load audit trail");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 dark:bg-slate-950 py-8" data-testid="audit-trail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-orange-600 dark:text-orange-400 mb-1 flex items-center gap-2">
              <Shield className="w-3 h-3" /> Governance
            </div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-slate-900 dark:text-white">Audit Trail</h1>
            <p className="text-sm text-slate-500 mt-1">Every write action, who did it, and when.</p>
          </div>
          <button onClick={load} className="text-xs border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-1.5 flex items-center gap-1.5 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-900" data-testid="audit-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total events"  value={summary.total} icon={Activity} testid="audit-total" />
            <StatCard label="Unique actions" value={summary.by_action.length} icon={Activity} testid="audit-actions" />
            <StatCard label="Entities"        value={summary.by_entity.length} icon={Activity} testid="audit-entities" />
            <StatCard label="Top actor"       value={summary.top_actors[0]?.email?.split("@")[0] || "—"}
                      sub={summary.top_actors[0] ? `${summary.top_actors[0].count} events` : ""}
                      icon={User} testid="audit-top-actor" />
          </div>
        )}

        {/* Filters */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 mb-6">
          <div className="flex items-center gap-2 mb-3 text-slate-700 dark:text-slate-200 font-semibold text-sm">
            <Filter className="w-4 h-4" /> Filters
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {["actor_email", "entity", "action", "entity_id"].map((k) => (
              <input key={k} placeholder={k.replace("_", " ")}
                     value={filters[k]}
                     onChange={(e) => setFilters(f => ({ ...f, [k]: e.target.value }))}
                     data-testid={`audit-filter-${k}`}
                     className="border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-950" />
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={load} className="btn-signature rounded-lg px-4 py-1.5 text-sm font-semibold" data-testid="audit-apply">
              Apply
            </button>
            <button onClick={() => { setFilters({ actor_email: "", entity: "", action: "", entity_id: "" }); setTimeout(load, 0); }}
                    className="rounded-lg px-4 py-1.5 text-sm font-semibold border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300">
              Clear
            </button>
          </div>
        </div>

        {/* Logs table */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading…</div>
          ) : err ? (
            <div className="p-8 text-center text-rose-600">{err}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 dark:bg-slate-950/50 text-slate-600 dark:text-slate-400">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">When</th>
                    <th className="text-left px-4 py-3 font-semibold">Actor</th>
                    <th className="text-left px-4 py-3 font-semibold">Action</th>
                    <th className="text-left px-4 py-3 font-semibold">Entity</th>
                    <th className="text-left px-4 py-3 font-semibold">IP</th>
                    <th className="text-left px-4 py-3 font-semibold">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.logs || []).map((r) => (
                    <tr key={r.audit_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`audit-row-${r.audit_id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">
                        {new Date(r.at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800 dark:text-slate-100">{r.actor_email || "—"}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">{r.actor_role || "—"}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-200">{r.action}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${ENTITY_TONE[r.entity] || "bg-slate-200"}`}>
                          {r.entity}
                        </span>
                        <div className="font-mono text-[11px] text-slate-500 mt-0.5">{r.entity_id}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{r.ip || "—"}</td>
                      <td className="px-4 py-3 text-[11px] text-slate-500 max-w-md">
                        <div className="line-clamp-2">
                          {r.old && <><span className="text-rose-500">−</span> {JSON.stringify(r.old).slice(0, 80)} </>}
                          {r.new && <><span className="text-emerald-500">+</span> {JSON.stringify(r.new).slice(0, 80)}</>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(!data?.logs || data.logs.length === 0) && (
                    <tr><td colSpan={6} className="text-center text-slate-400 py-10">No audit entries match your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="font-heading font-black text-2xl text-slate-900 dark:text-white mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
